// bm-design-system: grade badge primitive
import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

// Tone maps to meaning, not to brand: danger (red) for failing, signal (amber)
// for caution, success (green) for passing. `success` was added to the palette
// for this — without it, passing states drew in amber and read as a warning.
const gradeBadgeVariants = cva(
  "inline-flex shrink-0 items-center justify-center rounded-full border-2 font-display font-bold",
  {
    variants: {
      tone: {
        good: "border-success/40 bg-success-faded text-success-display",
        fair: "border-signal/40 bg-signal-faded text-signal-display",
        poor: "border-danger/40 bg-danger-faded text-danger-display",
      },
      size: {
        sm: "h-10 w-10 text-lg",
        md: "h-16 w-16 text-2xl",
        lg: "h-24 w-24 text-5xl",
      },
    },
    defaultVariants: {
      tone: "fair",
      size: "md",
    },
  },
);

const GRADE_TONE: Record<string, "good" | "fair" | "poor"> = {
  A: "good",
  B: "good",
  C: "fair",
  D: "poor",
  F: "poor",
};

export interface GradeBadgeProps
  extends Omit<React.HTMLAttributes<HTMLSpanElement>, "children">,
    Pick<VariantProps<typeof gradeBadgeVariants>, "size"> {
  grade: string;
}

const GradeBadge = React.forwardRef<HTMLSpanElement, GradeBadgeProps>(
  ({ className, grade, size, ...props }, ref) => {
    const tone = GRADE_TONE[grade.toUpperCase()] ?? "fair";
    return (
      <span
        ref={ref}
        className={cn(gradeBadgeVariants({ tone, size, className }))}
        {...props}
      >
        {grade}
      </span>
    );
  },
);
GradeBadge.displayName = "GradeBadge";

export { GradeBadge, gradeBadgeVariants };
